// src/scenes/users/CreateUser.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import * as yup from "yup";
import axios from "axios";
import Header from "../../components/Header";
import SimpleForm from "../../components/SimpleForm";
import { Snackbar, Alert } from "@mui/material";



/* ---------- Axios (CRA: REACT_APP_API_URL should include /api) ---------- */
const token = localStorage.getItem("token");

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL, 
  timeout: 15000,
  headers: {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  },
});

/* ---------- Helpers ---------- */
const buildInitials = (fields) =>
  fields.reduce((acc, f) => {
    if (f.default !== undefined) acc[f.name] = f.default;
    else if (f.type === "checkbox") acc[f.name] = false;
    else if (f.type === "number") acc[f.name] = 0;
    else acc[f.name] = "";
    return acc;
  }, {});

/* Validation: warehouseId optional (nullable), roleId required */
const USER_SCHEMA = yup.object().shape({
  fullName: yup.string().required("required"),
  userName: yup.string().required("required"),
  password: yup.string().min(6, "min 6 characters").required("required"),
  phone: yup
    .string()
    .matches(/^[0-9+()\-\s]{7,20}$/, "invalid phone")
    .required("required"),
  roleId: yup.number().typeError("select a role").required("required"),
  warehouseId: yup.number().typeError("invalid warehouse").nullable(),
});

/* ---------- API helpers ---------- */
async function getRoles() {
  // cache-buster param to avoid any proxy/browser caching of now-empty results
  const res = await api.get("/api/role", { params: { _t: Date.now() } });
  return res.data.data; // [{id,name}, ...] or []
}
async function getWarehouses() {
  const res = await api.get("/api/warehouse", { params: { _t: Date.now() } });
  return res.data.data; // [{id,name,...}, ...] or []
}
async function postUser(body) {
  try {
    const res = await api.post("/api/user", body); // POST /api/user
    return { data: res.data, error: null };
  } catch (err) {
    const responseData = err?.response?.data;
    const message =
      responseData?.error?.errors?.[0]?.message ||
      responseData?.message ||
      err.message ||
      "Request failed";
    return { data: responseData, error: message };
  }
}

/* ---------- Page ---------- */
export default function CreateUser() {
  const [toast, setToast] = useState({ open: false, msg: "", sev: "success" });
  const [roles, setRoles] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loadingLists, setLoadingLists] = useState(false);

  const mountedRef = useRef(true);

  const loadLists = async () => {
    setLoadingLists(true);
    try {
      const [r, w] = await Promise.allSettled([getRoles(), getWarehouses()]);
      if (!mountedRef.current) return;

      // Roles
      if (r.status === "fulfilled" && Array.isArray(r.value)) {
        setRoles(r.value);
      } else {
        setRoles([]); // clear on failure/invalid
      }

      // Warehouses
      if (w.status === "fulfilled" && Array.isArray(w.value)) {
        setWarehouses(w.value);
      } else {
        setWarehouses([]); // clear on failure/invalid
      }
    } catch {
      if (mountedRef.current) {
        setToast({ open: true, msg: "Failed to load dropdowns", sev: "error" });
        setRoles([]);
        setWarehouses([]);
      }
    } finally {
      if (mountedRef.current) setLoadingLists(false);
    }
  };

  // initial fetch + refetch on focus + periodic refetch
  useEffect(() => {
    mountedRef.current = true;
    loadLists();

    const onFocus = () => loadLists();
    window.addEventListener("focus", onFocus);

    const intervalId = setInterval(loadLists, 30000); // 30s

    return () => {
      mountedRef.current = false;
      window.removeEventListener("focus", onFocus);
      clearInterval(intervalId);
    };
  }, []);

  // Build fields (dropdown options depend on fetched lists)
  const FIELDS = useMemo(() => {
    const hasRoles = roles.length > 0;
    const hasWarehouses = warehouses.length > 0;

    const roleOptions = roles.map((r) => ({ label: r.name, value: r.id }));
    const whOptions = [{ label: "None", value: "" }].concat(
      warehouses.map((w) => ({ label: w.name ?? `Warehouse #${w.id}`, value: w.id }))
    );

    return [
      {
        name: "fullName",
        label: "Full Name",
        type: "text",
        span: 2,
        required: true,
        props: { placeholder: "eg. yohi mesfin" },
      },
      {
        name: "userName",
        label: "Username",
        type: "text",
        span: 2,
        required: true,
        props: { placeholder: "eg. yohi" },
      },
      {
        name: "password",
        label: "Password",
        type: "text",
        span: 2,
        required: true,
        props: { type: "password", placeholder: "min 6 characters" },
      },
      {
        name: "phone",
        label: "Phone",
        type: "text",
        span: 2,
        required: true,
        props: { placeholder: "eg. 0912345677" },
      },

      {
        name: "roleId",
        label: loadingLists ? "Loading roles..." : hasRoles ? "Role" : "No roles found",
        type: "select",
        span: 2,
        required: true,
        options: roleOptions,
        props: { disabled: loadingLists || !hasRoles },
      },
      {
        name: "warehouseId",
        label: loadingLists
          ? "Loading warehouses..."
          : hasWarehouses
          ? "Warehouse (optional)"
          : "No warehouses found",
        type: "select",
        span: 2,
        required: false,
        options: whOptions, // includes "None" -> ""
        props: { disabled: loadingLists || (!hasWarehouses && whOptions.length === 1) },
        default: "",
      },
    ];
  }, [roles, warehouses, loadingLists]);

  const INITIALS = useMemo(() => buildInitials(FIELDS), [FIELDS]);

  const handleSubmit = async (
  values,
  { setSubmitting, setErrors, resetForm }
) => {
 

  // backend payload
  const payload = {
    
    fullName: values.fullName,
    userName: values.userName,
    password: values.password,
    phone: values.phone,
    roleId: values.roleId,

    // optional warehouse
    warehouseId:
      values.warehouseId === "" ? null : values.warehouseId,
  };

  const { data, error } = await postUser(payload);

  setSubmitting(false);

  // username exists
  if (data?.message && /username.*exists/i.test(data.message)) {
    setErrors({ userName: data.message });

    setToast({
      open: true,
      msg: `Error: ${data.message}`,
      sev: "error",
    });

    return;
  }

  // sequelize validation
  const isSequelize =
    data?.error?.name?.includes("Sequelize") ||
    Array.isArray(data?.error?.errors);

  if (isSequelize) {
    const first = data?.error?.errors?.[0];

    const path = first?.path || "userName";
    const msg =
      first?.message || data?.message || "Validation error";

    setErrors({ [path]: msg });

    setToast({
      open: true,
      msg: `Error: ${msg}`,
      sev: "error",
    });

    return;
  }

  // generic error
  if (error) {
    setToast({
      open: true,
      msg: `Error: ${error}`,
      sev: "error",
    });

    return;
  }

  // success
  setToast({
    open: true,
    msg: data?.message || "User created successfully",
    sev: "success",
  });

  resetForm();
};

  return (
    <>
      <SimpleForm
        key={`roles-${roles.length}-wh-${warehouses.length}`} // force rerender when options change
        title="CREATE USER"
        subtitle="Add a new user"
        Header={Header}
        fields={FIELDS}
        initialValues={INITIALS}
        validation={USER_SCHEMA}
        onSubmit={handleSubmit}
        submitLabel="Create User"
        columns={4}
      />

      <Snackbar
       anchorOrigin={{ vertical: 'top', horizontal: 'right'}}
      sx={{ mt: 5 }}
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((s) => ({ ...s, open: false }))}
      >
        <Alert
          onClose={() => setToast((s) => ({ ...s, open: false }))}
          severity={toast.sev}
          variant="filled"
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </>
  );
}
